import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme
} from '@material-ui/core';
import ClassroomSelectSortIcon from 'components/@common/Table/TableSortIcon';
import ClassroomSelectRow, { ClassroomRowData } from './ClassroomSelectRow';
import ClassroomSelectTableHeadCell from './ClassroomSelectTableHeadCell';

const useStyles = makeStyles<Theme>(theme => ({
  iconColor: {
    color: theme.palette.secondary.main
  },
  headCell: {
    width: '200px',
    paddingLeft: 0,
    paddingRight: 0,
    borderBottom: 'none'
  },
  checkboxCell: {
    width: '0.2rem',
    padding: 0,
    paddingLeft: theme.spacing(1)
  },
  firstCell: {
    paddingRight: 0,
    paddingLeft: theme.spacing(1.5)
  },
  lastCell: {
    paddingLeft: 0,
    paddingRight: theme.spacing(1.5)
  },
  body: {
    boxShadow:
      '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 1px rgba(0,0,0,0.14), 0px 1px 3px 1px rgba(0,0,0,0.12)',
    borderRadius: theme.shape.borderRadius
  },
  iconCell: {
    width: '65px'
  },
  table: {
    borderCollapse: 'unset',
    margin: '0 auto',
    paddingBottom: theme.spacing(4)
  }
}));

export interface ClassSelectTableProps {
  classroomRowData: ClassroomRowData[];
  selectedClassroomIds: number[];
  handleCheck?: React.ChangeEventHandler<HTMLInputElement>;
  readOnly?: boolean;
}

type SortOrder = {
  direction: 'ASC' | 'DESC';
  sortKey: keyof Omit<ClassroomRowData, 'productCurriculums'> | null;
};

function sortByKey(
  direction: SortOrder['direction'],
  sortKey: SortOrder['sortKey'],
  a: ClassroomRowData,
  b: ClassroomRowData
): number {
  let comparison = 0;
  if (sortKey) {
    const keyA = a[sortKey] || '';
    const keyB = b[sortKey] || '';
    if (keyA !== keyB) {
      if (String(keyA).toUpperCase() < String(keyB).toUpperCase()) {
        comparison = direction === 'ASC' ? -1 : 1;
      } else {
        comparison = direction === 'ASC' ? 1 : -1;
      }
    }
  }

  return comparison;
}

const ClassroomSelectTable: React.FC<ClassSelectTableProps> = ({
  classroomRowData,
  handleCheck,
  selectedClassroomIds,
  readOnly
}) => {
  const classes = useStyles();
  const { t } = useTranslation('ClassSelectTable');
  const [sort, setSort] = React.useState<SortOrder>({ direction: 'ASC', sortKey: null });

  const handleSort = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      const { sortKey: activeSortKey, direction } = sort;
      if (event.currentTarget) {
        setSort({
          direction:
            event.currentTarget.id === activeSortKey && direction === 'ASC' ? 'DESC' : 'ASC',
          sortKey: event.currentTarget.id as keyof Omit<ClassroomRowData, 'productCurriculums'>
        });
      }
      classroomRowData.sort((a, b) => sortByKey(sort.direction, sort.sortKey, a, b));
    },
    [setSort, sort, classroomRowData]
  );

  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell className={cx(classes.headCell, classes.checkboxCell)} />
          <ClassroomSelectTableHeadCell
            className={cx(classes.headCell, classes.firstCell)}
            innerContainerProps={{ id: 'name' }}
            onClick={handleSort}
            SortIcon={
              <ClassroomSelectSortIcon
                className={classes.iconColor}
                direction={sort.sortKey === 'name' ? sort.direction : 'ASC'}
              />
            }
          >
            {t('CLASS NAME')}
          </ClassroomSelectTableHeadCell>
          <ClassroomSelectTableHeadCell
            className={classes.headCell}
            innerContainerProps={{ id: 'educator' }}
            onClick={handleSort}
            SortIcon={
              <ClassroomSelectSortIcon
                className={classes.iconColor}
                direction={sort.sortKey === 'educator' ? sort.direction : 'ASC'}
              />
            }
          >
            {t('EDUCATOR')}
          </ClassroomSelectTableHeadCell>
          <ClassroomSelectTableHeadCell
            className={classes.headCell}
            onClick={handleSort}
            innerContainerProps={{ id: 'coEducator' }}
            SortIcon={
              <ClassroomSelectSortIcon
                direction={sort.sortKey === 'coEducator' ? sort.direction : 'ASC'}
                className={classes.iconColor}
              />
            }
          >
            {t('CO-EDUCATOR')}
          </ClassroomSelectTableHeadCell>
          <ClassroomSelectTableHeadCell className={cx(classes.headCell)}>
            {t('STATUS')}
          </ClassroomSelectTableHeadCell>
          <ClassroomSelectTableHeadCell
            className={cx(classes.headCell, classes.iconCell, classes.lastCell)}
          >
            {t('PRODUCTS')}
          </ClassroomSelectTableHeadCell>
        </TableRow>
      </TableHead>
      <TableBody className={classes.body}>
        {classroomRowData.map((item: ClassroomRowData, i: number) => (
          <ClassroomSelectRow
            readOnly={readOnly}
            handleCheck={handleCheck}
            id={`${item.id}-${sort.direction}-${sort.sortKey}`}
            key={`${item.id}-remove-table-row`}
            classroomData={item}
            checked={selectedClassroomIds.includes(item.id)}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default ClassroomSelectTable;
